import { useTranslation } from 'react-i18next'
import { useScrollReveal } from '@/hooks/use-scroll-reveal'
import Button from '@/components/ui/button'
import Container from '@/components/ui/container'
import Icon from '@/components/ui/icon'
import Link from '@/components/ui/link'
import SectionHeader from '@/components/ui/section-header'

export default function Trust() {
  const { t } = useTranslation()
  const { ref, visible } = useScrollReveal()

  const items = t('trust.items', { returnObjects: true }) as {
    icon: string
    title: string
    description: string
  }[]

  return (
    <section ref={ref} className="py-16 md:py-24 lg:py-32 bg-surface-container-lowest">
      <Container>
        <div
          className={`max-w-3xl mb-12 md:mb-16 transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <SectionHeader
            label={t('trust.label')}
            heading={t('trust.heading')}
            description={t('trust.description')}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-outline-variant/10 border border-outline-variant/10">
          {items.map((item, i) => (
            <div
              key={i}
              className={`group bg-surface-container-low p-6 md:p-8 transition-all duration-500 ease-out hover:bg-surface-container ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: visible ? `${200 + i * 120}ms` : '0ms' }}
            >
              <Icon
                name={item.icon}
                className="text-primary text-3xl mb-5 block transition-transform duration-300 group-hover:-translate-y-1"
              />
              <h4 className="font-display text-base md:text-lg font-bold mb-3 tracking-tight text-on-surface">
                {item.title}
              </h4>
              <p className="text-on-surface-variant text-sm leading-relaxed font-light whitespace-pre-line">
                {item.description}
              </p>
            </div>
          ))}
        </div>

        <div
          className={`mt-12 md:mt-16 lg:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-10 transition-all duration-700 ease-out ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: visible ? '700ms' : '0ms' }}
        >
          <p className="text-on-surface-variant max-w-2xl text-base md:text-lg">
            {t('trust.cta.description')}
          </p>
          <Link href="/contact">
            <Button variant="gradient">{t('trust.cta.button')}</Button>
          </Link>
        </div>
      </Container>
    </section>
  )
}
